import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiErrorText } from '../api/errorText';
import { useAuth, useDistinctServerName } from '../auth/AuthContext';
import EmiroLogo from '../components/EmiroLogo';
import { Stage, StagePanel } from '../components/Stage';
import { headlineGradient } from '../theme';

export default function SignInPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { signIn } = useAuth();
  const serverName = useDistinctServerName();

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);

    try {
      await signIn(name, password);
    } catch (caught) {
      // The server answers a wrong name and a wrong password identically; so does this.
      setError(apiErrorText(caught, t).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stage>
      <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center', mb: 4 }}>
        <EmiroLogo layout="mark" size={76} glow />
        {/* The server's own name where it has one, written the way the website writes its
            headline; the product's name where it has not. */}
        <Typography
          variant="h1"
          sx={{
            pt: 1,
            fontSize: { xs: '2.5rem', sm: '3.25rem' },
            letterSpacing: '-0.045em',
            lineHeight: 1,
            ...(theme.palette.mode === 'dark' && {
              background: headlineGradient,
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
            }),
          }}
        >
          {serverName ?? t('app.name')}
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: '1.0625rem' }}>
          {t('signIn.subtitle')}
        </Typography>
      </Stack>

      <StagePanel maxWidth={420}>
        <Stack component="form" onSubmit={submit} spacing={2.5}>

          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label={t('signIn.name')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoComplete="username"
            autoFocus
            required
          />

          <TextField
            label={t('signIn.password')}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            type="password"
            autoComplete="current-password"
            required
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={busy || !name || !password}
            sx={{ mt: 1 }}
          >
            {t('signIn.submit')}
          </Button>
        </Stack>
      </StagePanel>
    </Stage>
  );
}
